import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const RSS_LOGO = "https://customer-assets.emergentagent.com/job_199c36c2-e07b-4567-84a2-e99d8a5503d6/artifacts/c9vy7sfe_rumpun_silat_sarawak_logo_transparent_1024.png";

const navItems = [
  { label: 'Utama', to: '/' },
  { label: 'Tentang RSS', to: '/tentang-rss' },
  { label: 'Kepimpinan', to: '/kepimpinan' },
  { label: 'Wilayah', to: '/wilayah' },
  { label: 'Aktiviti', to: '/aktiviti' },
  { label: 'Pencapaian', to: '/pencapaian' },
  { label: 'Direktori', to: '/direktori-perguruan' },
  { label: 'Galeri', to: '/galeri' },
  { label: 'Hubungi', to: '/hubungi' },
];

export const PublicLayout = ({ children }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  const handlePortalAhli = () => {
    if (user) {
      navigate(user.role === 'RSS_ADMIN' ? '/admin/dashboard' : '/school/dashboard');
    } else {
      navigate('/portal-ahli');
    }
  };

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif" }} className="min-h-screen bg-white text-[#0B0B0B]">

      {/* Top accent bar */}
      <div className="h-1 bg-gradient-to-r from-[#D71920] via-[#FFD21F] to-[#D71920]" />

      {/* Header */}
      <header
        className={`sticky top-0 z-50 bg-white transition-all duration-200 ${
          scrolled ? 'shadow-md border-b-0' : 'border-b border-[#E5E5E5]'
        }`}
        data-testid="public-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">

            {/* Logo + Org Name */}
            <Link to="/" className="flex items-center gap-2.5 flex-shrink-0" data-testid="header-logo-link">
              <img
                src={RSS_LOGO}
                alt="Rumpun Silat Sarawak"
                className="h-10 w-10 object-contain"
              />
              <div className="hidden sm:block leading-tight">
                <div className="text-[13px] font-bold text-[#0B0B0B] tracking-tight leading-none">RUMPUN SILAT</div>
                <div className="text-[11px] font-medium text-[#D71920] tracking-widest leading-none mt-0.5">SARAWAK</div>
              </div>
            </Link>

            {/* Desktop Nav — visible at xl (1280px+) */}
            <nav className="hidden xl:flex items-center gap-0.5" data-testid="desktop-nav">
              {navItems.map((item) => {
                const isActive =
                  item.to === '/'
                    ? location.pathname === '/'
                    : location.pathname.startsWith(item.to);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`px-3 py-2 text-[13px] font-medium rounded transition-colors ${
                      isActive
                        ? 'text-[#D71920]'
                        : 'text-[#555555] hover:text-[#0B0B0B]'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            {/* CTA + Mobile Toggle */}
            <div className="flex items-center gap-2">
              <Link
                to="/register-school"
                className="hidden lg:inline-flex items-center bg-white border border-[#E5E5E5] hover:border-[#D71920] text-[#0B0B0B] hover:text-[#D71920] text-[12px] font-semibold px-3 py-1.5 rounded transition-colors"
                data-testid="daftar-perguruan-header-btn"
              >
                Daftar Perguruan
              </Link>
              <button
                onClick={handlePortalAhli}
                className="bg-[#FFD21F] hover:bg-[#f5c800] text-[#0B0B0B] text-[13px] font-semibold px-4 py-2 rounded transition-colors"
                data-testid="portal-ahli-header-btn"
              >
                {user ? 'Dashboard' : 'Portal Ahli'}
              </button>
              <button
                className="xl:hidden p-2 text-[#0B0B0B] hover:bg-[#F7F7F7] rounded"
                onClick={() => setMenuOpen(!menuOpen)}
                data-testid="mobile-menu-toggle"
                aria-label="Toggle menu"
              >
                {menuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="xl:hidden bg-white border-t border-[#E5E5E5]" data-testid="mobile-nav-menu">
            <div className="max-w-7xl mx-auto px-4 py-3">
              {navItems.map((item) => {
                const isActive =
                  item.to === '/'
                    ? location.pathname === '/'
                    : location.pathname.startsWith(item.to);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`block py-3 text-sm border-b border-[#F7F7F7] font-medium ${
                      isActive ? 'text-[#D71920]' : 'text-[#555555]'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
              <div className="py-4">
                <button
                  onClick={handlePortalAhli}
                  className="w-full bg-[#FFD21F] hover:bg-[#f5c800] text-[#0B0B0B] text-sm font-semibold py-2.5 rounded transition-colors"
                >
                  {user ? 'Ke Dashboard' : 'Portal Ahli'}
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Page Content */}
      <main>{children}</main>

      {/* Footer */}
      <footer className="bg-[#0B0B0B] text-white" data-testid="public-footer">
        <div className="h-0.5 bg-gradient-to-r from-[#D71920] via-[#FFD21F] to-[#D71920]" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">

            {/* Brand column */}
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-5">
                <img src={RSS_LOGO} alt="RSS" className="h-16 w-16 object-contain" />
                <div>
                  <div className="font-bold text-white text-base leading-tight">Rumpun Silat Sarawak</div>
                  <div className="text-[#FFD21F] text-xs tracking-widest mt-0.5">BERSENI BERBUDI BERBAKTI</div>
                </div>
              </div>
              <p className="text-[#999] text-sm leading-relaxed max-w-xs">
                Badan induk rasmi persilatan Negeri Sarawak yang menyatukan perguruan, pesilat, guru dan jurulatih seni silat di seluruh Sarawak.
              </p>
              <p className="text-[#555] text-xs mt-3">No. Daftar: PPM-010-13-02091987</p>
            </div>

            {/* Links */}
            <div className="md:col-span-3">
              <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-widest">Pautan Pantas</h4>
              <ul className="space-y-2.5">
                {[
                  { label: 'Tentang RSS', to: '/tentang-rss' },
                  { label: 'Kepimpinan', to: '/kepimpinan' },
                  { label: 'Wilayah', to: '/wilayah' },
                  { label: 'Pencapaian', to: '/pencapaian' },
                  { label: 'Aktiviti', to: '/aktiviti' },
                  { label: 'Direktori Perguruan', to: '/direktori-perguruan' },
                  { label: 'Galeri', to: '/galeri' },
                  { label: 'Hubungi Kami', to: '/hubungi' },
                ].map(l => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-[#999] hover:text-[#FFD21F] text-sm transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Portal */}
            <div className="md:col-span-4">
              <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-widest">Portal Digital</h4>
              <ul className="space-y-2.5">
                {[
                  { label: 'Portal Ahli (Log Masuk)', to: '/portal-ahli' },
                  { label: 'Daftar Perguruan', to: '/register-school' },
                  { label: 'Daftar Ahli / Pesilat', to: '/register-member' },
                  { label: 'Verifikasi QR', to: '/verify' },
                ].map(l => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-[#999] hover:text-[#FFD21F] text-sm transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="mt-6 p-4 bg-white/5 rounded border border-white/10">
                <p className="text-[#999] text-xs leading-relaxed">
                  Untuk sebarang pertanyaan, sila hubungi Sekretariat RSS Sarawak.
                </p>
                <Link to="/hubungi" className="text-[#FFD21F] text-xs font-medium mt-2 inline-block hover:underline">
                  Hubungi Kami →
                </Link>
              </div>
            </div>
          </div>

          {/* Bottom */}
          <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-[#555] text-xs">
              © {new Date().getFullYear()} Rumpun Silat Sarawak. Hak Cipta Terpelihara.
            </p>
            <p className="text-[#555] text-xs italic">
              Memartabatkan Warisan Seni Silat Sarawak
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
